import DOMPurify from "dompurify";

const ALLOWED_WEB_PROTOCOLS = new Set(["http:", "https:"]);

const DANGEROUS_SRC_PREFIX = /^\s*(javascript|data|vbscript):/i;

/** HTML エスケープ */
export const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** http / https のみ許可（空は不可） */
export const isAllowedWebUrl = (url: string): boolean => {
  if (!url || url.trim() === "") {
    return false;
  }

  try {
    const parsed = new URL(url.trim());
    return ALLOWED_WEB_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
};

/** お知らせ img の src（Storage 相対パスまたは http(s)） */
export const isAllowedAnnounceImgSrc = (src: string): boolean => {
  if (!src || DANGEROUS_SRC_PREFIX.test(src)) {
    return false;
  }

  if (isAllowedWebUrl(src)) {
    return true;
  }

  return /^public\/announce\/[^"'<>\s]+$/.test(src.trim());
};

const stickyNoteHtmlPurify = DOMPurify();

const announceHtmlPurify = DOMPurify();

const QL_INDENT_CLASS = /^ql-indent-(?:[1-9]|1[0-5])$/;
const ALLOWED_COLOR_STYLE =
  /^color:\s*(#[0-9a-fA-F]{3,8}|rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\))\s*;?$/i;

announceHtmlPurify.addHook("uponSanitizeAttribute", (node, data) => {
  if (!data.attrValue) {
    return;
  }

  if (data.attrName === "href") {
    if (!isAllowedWebUrl(data.attrValue)) {
      data.attrValue = "";
    }
  } else if (data.attrName === "src" && node.tagName === "IMG") {
    if (!isAllowedAnnounceImgSrc(data.attrValue)) {
      data.attrValue = "";
    }
  } else if (data.attrName === "class") {
    data.attrValue = data.attrValue
      .split(/\s+/)
      .filter((c) => QL_INDENT_CLASS.test(c))
      .join(" ");
  } else if (data.attrName === "style") {
    const normalized = data.attrValue.trim();
    if (!ALLOWED_COLOR_STYLE.test(normalized)) {
      data.attrValue = "";
    }
  }
});

const ANNOUNCE_ALLOWED_TAGS = [
  "h1",
  "h2",
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "del",
  "blockquote",
  "ol",
  "ul",
  "li",
  "span",
  "a",
  "img"
];

const ANNOUNCE_ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "class",
  "style"
];

/**
 * エスケープ済みテキスト内の URL を安全な a タグに変換
 */
export const linkifyEscapedText = (
  escapedText: string,
  urlPattern: RegExp
): string => {
  const flags = urlPattern.flags.includes("g")
    ? urlPattern.flags
    : `${urlPattern.flags}g`;
  const pattern = new RegExp(urlPattern.source, flags);

  return escapedText.replace(pattern, (match) => {
    if (!isAllowedWebUrl(match)) {
      return match;
    }

    const href = escapeHtml(match);
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });
};

/** StickyNote InfoWindow 用 HTML */
export const sanitizeStickyNoteHtml = (html: string): string =>
  stickyNoteHtmlPurify.sanitize(html, {
    ALLOWED_TAGS: ["div", "a", "button", "img"],
    ALLOWED_ATTR: ["href", "target", "rel", "class", "id", "alt", "src"]
  });

/** Quill お知らせ本文用 */
export const sanitizeAnnounceHtml = (html: string): string => {
  if (!html) {
    return "";
  }

  return announceHtmlPurify.sanitize(html, {
    ALLOWED_TAGS: ANNOUNCE_ALLOWED_TAGS,
    ALLOWED_ATTR: ANNOUNCE_ALLOWED_ATTR
  });
};

const agreementHtmlPurify = DOMPurify();

const AGREEMENT_TEXT_ALIGN_STYLE = /^text-align:\s*(center|right)\s*;?$/i;

agreementHtmlPurify.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName === "style" && node.tagName === "DIV") {
    const normalized = data.attrValue.trim();
    if (!AGREEMENT_TEXT_ALIGN_STYLE.test(normalized)) {
      data.attrValue = "";
    }
  }
});

const AGREEMENT_ALLOWED_TAGS = [
  "br",
  "div",
  "p",
  "ol",
  "ul",
  "li",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "del",
  "blockquote",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6"
];

/** 利用規約 Markdown 内の生 HTML 断片用（rehype-raw 前に適用） */
export const sanitizeAgreementHtml = (html: string): string => {
  if (!html) {
    return "";
  }

  return agreementHtmlPurify.sanitize(html, {
    ALLOWED_TAGS: AGREEMENT_ALLOWED_TAGS,
    ALLOWED_ATTR: ["style"]
  });
};
